import { useMemo, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, Ban, CheckCircle2, Clock3, RotateCcw } from 'lucide-react'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { ExecutionStateBadge } from '@/components/execution/ExecutionRow'
import {
  agentJobExecutionState,
  formatCost,
  formatDurationMs,
  formatZonedDateTime,
  JOB_BLOCKED_REASON_KEYS,
  minorUnitsCost,
} from '@/components/execution/executionState'
import { builderCalistoHref } from '@/utils/contextUrl'
import type {
  AgentJobApproval,
  AgentJobArtifact,
  AgentJobCostResponse,
  AgentJobDetailResponse,
  AgentJobEvent,
  AgentJobResult,
} from '@/types/agentJob'

/** Liens des sections Approbations et Provenance ; le client de la page les remplace par ses routes. */
export interface AgentJobInspectorLinks {
  /** Page À traiter, où se prend la décision d'une approbation en attente. */
  inbox: string
  /** Activité, filtrée par plan (`graphId`) ou par job (`jobId`). */
  activity: (params: Record<string, string>) => string
  /** Fil Studio du Chef d'orchestre. */
  studioThread: (threadId: string, agentId?: string) => string
}

const DEFAULT_LINKS: AgentJobInspectorLinks = {
  inbox: '/run/travail/inbox',
  activity: (params) => `/run/travail/activity?${new URLSearchParams(params).toString()}`,
  studioThread: (threadId, agentId) => builderCalistoHref(null, agentId, { threadId }),
}

interface AgentJobInspectorProps {
  detail?: AgentJobDetailResponse
  result?: AgentJobResult | null
  artifacts?: AgentJobArtifact[]
  cost?: AgentJobCostResponse
  events: AgentJobEvent[]
  isLoading: boolean
  isCancelling: boolean
  isRetrying: boolean
  error?: string | null
  retryParentHref?: string
  links?: Partial<AgentJobInspectorLinks>
  onCancel: () => void
  onRetry: () => void
}

type Translate = (key: string, options?: Record<string, unknown>) => string

function blockedReasonLabel(reason: string, t: Translate) {
  const key = JOB_BLOCKED_REASON_KEYS[reason]
  return key ? t(key) : t('agentJob.blockedReasonUnknown', { code: reason })
}

// Le résumé ne montre que du texte lisible ; les valeurs structurées restent
// dans le détail technique (OS-06 : pas de dump JSON comme résumé).
function readableEntries(result?: AgentJobResult | null) {
  return Object.entries(result?.content ?? {}).filter((entry): entry is [string, string] => typeof entry[1] === 'string' && entry[1].trim() !== '')
}

// Statut lisible d'une approbation : le serveur distingue l'attente, la décision et l'expiration.
type ApprovalTone = 'waiting' | 'approved' | 'rejected' | 'expired' | 'completed' | 'unknown'

function approvalTone(approval: AgentJobApproval): ApprovalTone {
  if (approval.status === 'waiting') return 'waiting'
  if (approval.status === 'expired') return 'expired'
  if (approval.status === 'completed') {
    if (approval.decision === 'approved') return 'approved'
    if (approval.decision === 'rejected' || approval.decision === 'refused') return 'rejected'
    return 'completed'
  }
  return 'unknown'
}

// Étape = identifiant de l'action de workflow, sinon « Approbation initiale ».
function approvalStepLabel(approval: AgentJobApproval, t: Translate) {
  if (approval.workflow_action_id) return approval.workflow_action_id
  return approval.phase === 'workflow_step' ? t('work.activity.approvals.workflowStep') : t('work.activity.approvals.initial')
}

// Libellé lisible du principal (qui agit) ; null si le serveur n'en envoie pas.
function principalLabel(principal: { kind?: string } | undefined, t: Translate) {
  const kind = principal?.kind
  if (!kind) return null
  return t(`work.activity.principal.${kind}`, { defaultValue: t('work.activity.principal.unknown') })
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return <div><dt className="text-xs text-muted-foreground">{label}</dt><dd className="text-sm">{children}</dd></div>
}

export default function AgentJobInspector({
  detail,
  result,
  artifacts = [],
  cost,
  events,
  isLoading,
  isCancelling,
  isRetrying,
  error,
  retryParentHref,
  links: linkOverrides,
  onCancel,
  onRetry,
}: AgentJobInspectorProps) {
  const { t, i18n } = useTranslation()
  const locale = i18n?.language
  const links: AgentJobInspectorLinks = { ...DEFAULT_LINKS, ...linkOverrides }
  const [tab, setTab] = useState<'summary' | 'trajectory' | 'evidence'>('summary')
  const job = detail?.job.job
  const stopStatus = job?.runtime_state?.stop_status ?? 'not_requested'
  const stopLabel = useMemo(() => {
    if (stopStatus === 'requested') return t('agentJob.stopRequested')
    if (stopStatus === 'confirmed') return t('agentJob.stopConfirmed')
    if (stopStatus === 'unknown') return t('agentJob.stopUnknown')
    return t('agentJob.stopNotRequested')
  }, [stopStatus, t])

  if (isLoading && !detail) {
    return <Card><CardContent className="p-6 text-sm text-muted-foreground">{t('agentJob.detailLoading')}</CardContent></Card>
  }
  if (!job) {
    return <Card><CardContent className="p-6 text-sm text-muted-foreground">{error || t('agentJob.select')}</CardContent></Card>
  }

  const notAvailable = t('work.execution.notAvailable')
  const snapshot = detail.job
  const lease = snapshot.lease
  const state = agentJobExecutionState(job.status)
  const retryBlocked = job.runtime_state?.stop_status === 'unknown'
  const started = formatZonedDateTime(job.created_at, locale) ?? notAvailable
  const runtime = formatDurationMs(result?.metrics?.duration_ms)
  const costText = formatCost(cost ? minorUnitsCost(cost.minor_units, cost.currency) : minorUnitsCost(snapshot.cumulative_cost_minor_units, job.execution_policy?.currency), locale)
  // Provenance et approbations : absentes sur un ancien serveur, donc lues défensivement.
  const provenance = detail.provenance
  const approvals = detail.approvals ?? []
  const initiator = provenance?.initiator ?? job.initiator
  const initiatorKind = initiator?.kind
  const initiatorLabel = initiatorKind ? t(`work.activity.initiator.${initiatorKind}`, { defaultValue: t('work.activity.initiator.unknown') }) : t('work.activity.initiator.unknown')
  const ownerLabel = principalLabel(provenance?.principal ?? job.principal, t) ?? initiatorLabel
  const attemptText = typeof job.max_attempts === 'number' ? t('work.activity.attemptValue', { current: snapshot.attempts, max: job.max_attempts }) : String(snapshot.attempts)
  const leaseStatus = lease?.status ? t(`work.activity.leaseStatus.${lease.status}`, { defaultValue: lease.status }) : t('work.activity.leaseStatus.none')
  const texts = readableEntries(result)

  const technical: Array<[string, string | number | undefined]> = [
    [t('work.activity.jobId'), job.job_id],
    [t('work.activity.attemptId'), lease?.attempt_id],
    [t('work.activity.leaseId'), lease?.lease_id],
    [t('work.activity.fencingToken'), lease?.fencing_token],
    [t('agentJob.leaseDeadline'), lease?.expires_at ? formatZonedDateTime(lease.expires_at, locale) ?? lease.expires_at : undefined],
    [t('agentJob.principalId'), job.principal ? `${job.principal.kind ?? '—'} · ${job.principal.issuer ?? '—'} · ${job.principal.id ?? '—'}` : undefined],
    ['initiator.ref', initiator?.ref],
    [t('agentJob.hardDeadline'), job.hard_deadline_at ? formatZonedDateTime(job.hard_deadline_at, locale) ?? job.hard_deadline_at : undefined],
    [t('agentJob.specHash'), job.agent_spec_sha256],
    [t('agentJob.revision'), snapshot.revision],
    [t('agentJob.runtimeExecution'), snapshot.recipe_execution_id],
    [t('work.activity.provenance.agent'), provenance?.agent_id],
    [t('work.activity.provenance.plan'), provenance?.execution_plan?.graph_id],
    [t('work.activity.provenance.node'), provenance?.execution_plan?.node_key],
    [t('work.activity.provenance.thread'), provenance?.studio_thread?.thread_id],
    [t('work.activity.provenance.parentJob'), provenance?.parent_job_id],
    ...approvals.map((approval): [string, string] => [t('work.activity.approvals.actionId'), approval.action_id]),
  ]

  return (
    <Card data-testid="agent-job-inspector">
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div className="min-w-0">
          <CardTitle className="truncate text-lg">{job.goal || job.runtime_ref?.workflow_id || t('agentJob.unknown')}</CardTitle>
          <p className="mt-1 text-xs text-muted-foreground">{t(`agentJob.status.${job.status}`, { defaultValue: job.status })}</p>
        </div>
        <ExecutionStateBadge state={state} />
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" disabled={!detail.actions.cancel || isCancelling} onClick={onCancel}>
            <Ban className="mr-2 h-4 w-4" />{isCancelling ? t('agentJob.cancelling') : t('agentJob.cancel')}
          </Button>
          <Button size="sm" variant="outline" disabled={!detail.actions.retry || retryBlocked || isRetrying} onClick={onRetry}>
            <RotateCcw className="mr-2 h-4 w-4" />{isRetrying ? t('agentJob.retrying') : t('agentJob.retry')}
          </Button>
        </div>
        {retryBlocked ? <p className="flex items-center gap-2 text-sm text-warning"><AlertTriangle className="h-4 w-4" />{t('agentJob.retryBlocked')}</p> : null}
        {error ? <p className="text-sm text-destructive">{error}</p> : null}
        {job.blocked_reason ? <p className="text-sm text-warning">{t('agentJob.blockedReason')}: {blockedReasonLabel(job.blocked_reason, t)}</p> : null}
        {job.error?.message ? <p className="text-sm text-destructive">{t('agentJob.errorDetail')}: {job.error.message}</p> : null}
        {job.retry_of_job_id ? <p className="text-sm text-muted-foreground">{t('agentJob.retryOf')} {retryParentHref ? <a className="font-mono underline" href={retryParentHref}>{job.retry_of_job_id}</a> : <span className="font-mono">{job.retry_of_job_id}</span>}</p> : null}
        <div className="flex flex-wrap gap-2 border-b border-border pb-2" role="tablist">
          {(['summary', 'trajectory', 'evidence'] as const).map((id) => (
            <Button key={id} size="sm" role="tab" aria-selected={tab === id} variant={tab === id ? 'default' : 'ghost'} onClick={() => setTab(id)}>
              {t(`travail.${id}`)}
            </Button>
          ))}
        </div>
        {result?.validation?.schema_valid === false ? <p className="flex items-center gap-2 text-sm text-destructive"><AlertTriangle className="h-4 w-4" />{t('agentJob.invalidResult')}</p> : null}
        {tab === 'summary' ? (
          <section className="space-y-4" data-testid="agent-job-summary">
            <p className="text-sm">{t('work.activity.summaryLine', { state: t(`work.execution.state.${state}`), started })}</p>
            <dl className="grid gap-3 sm:grid-cols-2">
              <Field label={t('work.activity.owner')}>{ownerLabel}</Field>
              <Field label={t('work.activity.attempt')}>{attemptText}</Field>
              <Field label={t('work.activity.lease')}>{leaseStatus}{lease?.holder_ref ? <span className="text-muted-foreground"> · {t('work.activity.leaseHolder')} {lease.holder_ref}</span> : null}</Field>
              <Field label={t('agentJob.stopStatus')}><span className="flex items-center gap-1"><Clock3 className="h-4 w-4" />{stopLabel}</span></Field>
              <Field label={t('work.execution.runtime')}>{runtime ?? notAvailable}</Field>
              <Field label={t('work.execution.cost')}>{costText ?? notAvailable}</Field>
              <Field label={t('agentJob.workflow')}>{job.runtime_ref?.workflow_id || notAvailable}{job.runtime_ref?.workflow_version ? ` · ${job.runtime_ref.workflow_version}` : ''}</Field>
            </dl>
            {approvals.length ? (
              <section className="space-y-2" data-testid="agent-job-approvals">
                <h3 className="text-sm font-semibold">{t('work.activity.approvals.title')}</h3>
                <ul className="space-y-2">
                  {approvals.map((approval) => {
                    const tone = approvalTone(approval)
                    return (
                      <li key={approval.action_id} data-testid={`agent-job-approval-${approval.action_id}`} className="rounded-md border border-border px-3 py-2 text-sm">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-medium">{approvalStepLabel(approval, t)}</span>
                          <Badge variant={tone === 'waiting' ? 'secondary' : 'outline'}>{t(`work.activity.approvals.status.${tone}`)}</Badge>
                        </div>
                        <dl className="mt-1 space-y-0.5 text-xs text-muted-foreground">
                          <div>{t('work.activity.approvals.requested')} : {formatZonedDateTime(approval.requested_at, locale) ?? notAvailable}</div>
                          {approval.resolved_at ? <div>{t('work.activity.approvals.resolved')} : {formatZonedDateTime(approval.resolved_at, locale) ?? approval.resolved_at}</div> : null}
                          {approval.expires_at ? <div>{t('work.activity.approvals.expires')} : {formatZonedDateTime(approval.expires_at, locale) ?? approval.expires_at}</div> : null}
                        </dl>
                        {tone === 'waiting' ? <a className="mt-2 inline-flex text-xs font-medium underline" href={links.inbox}>{t('work.activity.approvals.openInbox')}</a> : null}
                      </li>
                    )
                  })}
                </ul>
              </section>
            ) : null}
            {provenance ? (
              <section className="space-y-2" data-testid="agent-job-provenance">
                <h3 className="text-sm font-semibold">{t('work.activity.provenance.title')}</h3>
                <dl className="grid gap-3 sm:grid-cols-2">
                  <Field label={t('work.activity.provenance.launchedBy')}>{principalLabel(provenance.principal, t) ?? notAvailable}</Field>
                  <Field label={t('work.activity.provenance.via')}>{initiatorLabel}</Field>
                  <Field label={t('work.activity.provenance.agent')}>{provenance.agent_id ? t('work.activity.provenance.agentDeployed') : t('work.activity.provenance.none')}</Field>
                  <Field label={t('work.activity.provenance.plan')}>{provenance.execution_plan?.graph_id ? <a className="underline" href={links.activity({ graphId: provenance.execution_plan.graph_id })}>{t('work.activity.provenance.openPlan')}</a> : t('work.activity.provenance.none')}</Field>
                  <Field label={t('work.activity.provenance.thread')}>{provenance.studio_thread?.thread_id ? <a className="underline" href={links.studioThread(provenance.studio_thread.thread_id, provenance.studio_thread.agent_id)}>{t('work.activity.provenance.openThread')}</a> : t('work.activity.provenance.none')}</Field>
                  <Field label={t('work.activity.provenance.parentJob')}>{provenance.parent_job_id ? <a className="underline" href={links.activity({ jobId: provenance.parent_job_id })}>{t('work.activity.provenance.openParentJob')}</a> : t('work.activity.provenance.none')}</Field>
                </dl>
              </section>
            ) : null}
            <div>
              <h3 className="mb-2 text-sm font-semibold">{t('agentJob.result')}</h3>
              {!result ? <p className="text-sm text-muted-foreground">{t('agentJob.noResult')}</p> : texts.length ? (
                <ul className="space-y-1 text-sm">{texts.map(([key, value]) => <li key={key} className="break-words">{value}</li>)}</ul>
              ) : <p className="text-sm text-muted-foreground">{t('work.activity.noResultText')}</p>}
            </div>
          </section>
        ) : null}
        {tab === 'trajectory' ? (
          <section data-testid="agent-job-trajectory">
            {events.length ? (
              <ol className="space-y-2">
                {events.map((event) => (
                  <li key={`${event.sequence}-${event.event_id}`} className="flex gap-3 rounded-md border border-border px-3 py-2 text-sm">
                    {['warning', 'error'].includes(event.severity || '') ? <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" /> : event.type === 'job.completed' ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" /> : <Clock3 className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />}
                    <div><p className="font-medium">{event.type}</p><p className="text-xs text-muted-foreground">#{event.sequence} · {formatZonedDateTime(event.at, locale) ?? event.at}</p></div>
                  </li>
                ))}
              </ol>
            ) : <p className="text-sm text-muted-foreground">{t('agentJob.noEvents')}</p>}
          </section>
        ) : null}
        {tab === 'evidence' ? (
          <section className="space-y-3" data-testid="agent-job-evidence">
            <h3 className="text-sm font-semibold">{t('agentJob.artifacts')}</h3>
            {artifacts.length ? (
              <ul className="space-y-2 text-sm">
                {artifacts.map((artifact) => (
                  <li key={`${artifact.name}-${artifact.sha256}`} className="rounded-md border border-border px-3 py-2">
                    <span className="font-medium">{artifact.name}</span>
                    <dl className="mt-1 space-y-1 text-xs text-muted-foreground">
                      <div><dt className="inline font-medium">{t('agentJob.artifactRef')}: </dt><dd className="inline break-all">{artifact.ref}</dd></div>
                      <div><dt className="inline font-medium">{t('agentJob.artifactSha256')}: </dt><dd className="inline break-all">{artifact.sha256 || '—'}</dd></div>
                    </dl>
                  </li>
                ))}
              </ul>
            ) : <p className="text-sm text-muted-foreground">{t('agentJob.noArtifacts')}</p>}
            {cost ? <p className="text-xs text-muted-foreground">{t('agentJob.cost')}: {cost.minor_units} {cost.currency}</p> : null}
          </section>
        ) : null}
        <details className="rounded-md border border-border px-3 py-2 text-xs" data-testid="agent-job-technical">
          <summary className="cursor-pointer text-sm font-medium">{t('work.execution.technical')}</summary>
          <dl className="mt-2 space-y-1">
            {technical.filter(([, value]) => value !== undefined && value !== '').map(([label, value], index) => (
              <div key={`${label}-${index}`} className="flex min-w-0 gap-2"><dt className="shrink-0 text-muted-foreground">{label}</dt><dd className="min-w-0 break-all font-mono">{String(value)}</dd></div>
            ))}
          </dl>
          {result ? (
            <pre className="mt-2 max-h-64 overflow-auto rounded-md border border-border bg-muted/30 p-2 font-mono text-[11px]">{JSON.stringify(result.content ?? {}, null, 2)}</pre>
          ) : null}
        </details>
      </CardContent>
    </Card>
  )
}
